/**
 * ContactUs — help form and contact details.
 *
 * Parity note: no contact endpoint exists on either platform yet, so the form
 * validates locally and confirms with a toast, exactly like mobile.
 */
import { useState, type FormEvent } from 'react';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import { ToastService } from '@wlb/shared/platform/notifier';
import { validateEmail, validateName, validateRequired } from '@wlb/shared/utils/validation';
import { Button } from '../components/ui/Button';
import { FormError } from '../components/ui/FormError';
import { MailIcon, MapPinIcon } from '../components/ui/icons';
import { MorePage } from '../features/more/MorePage';
import '../features/settings/settings.css';
import '../features/support/support.css';

const CATEGORY_KEYS = [
  'generalInquiry',
  'technicalSupport',
  'billing',
  'feedback',
  'bugReport',
] as const;

export function ContactUs() {
  const { t } = useTranslation();
  const [firstName, setFirstName] = useState('');
  const [email, setEmail] = useState('');
  const [category, setCategory] = useState<string>('');
  const [message, setMessage] = useState('');
  const [dirty, setDirty] = useState(false);

  const firstNameResult = validateName(firstName);
  const emailResult = validateEmail(email);
  const messageResult = validateRequired(message);

  const firstNameError = dirty && !firstNameResult.valid ? t(firstNameResult.message) : '';
  const emailError = dirty && !emailResult.valid ? t(emailResult.message) : '';
  const messageError = dirty && !messageResult.valid ? t(messageResult.message) : '';

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setDirty(true);
    if (!firstNameResult.valid || !emailResult.valid || !messageResult.valid) return;

    setFirstName('');
    setEmail('');
    setCategory('');
    setMessage('');
    setDirty(false);
    ToastService.show({ type: 'success', message: t('contactUs.submitSuccess') });
  };

  const supportEmail = t('contactUs.emailSupportAddress');

  return (
    <MorePage title={t('contactUs.title')} subtitle={t('contactUs.subheading')}>
      <h2 className="support-heading">{t('contactUs.heading')}</h2>

      <form className="settings-form" onSubmit={submit} noValidate>
        <label className="settings-field">
          <span className="settings-field__label">{t('contactUs.firstName')}</span>
          <input
            className={`settings-input${firstNameError ? ' settings-input--error' : ''}`}
            type="text"
            value={firstName}
            onChange={event => setFirstName(event.target.value)}
            placeholder={t('contactUs.firstNamePlaceholder')}
            autoComplete="given-name"
            aria-invalid={Boolean(firstNameError)}
            aria-describedby={firstNameError ? 'contact-first-name-error' : undefined}
          />
          <FormError message={firstNameError} id="contact-first-name-error" />
        </label>

        <label className="settings-field">
          <span className="settings-field__label">{t('contactUs.email')}</span>
          <input
            className={`settings-input${emailError ? ' settings-input--error' : ''}`}
            type="email"
            value={email}
            onChange={event => setEmail(event.target.value)}
            placeholder={t('contactUs.emailPlaceholder')}
            autoComplete="email"
            aria-invalid={Boolean(emailError)}
            aria-describedby={emailError ? 'contact-email-error' : undefined}
          />
          <FormError message={emailError} id="contact-email-error" />
        </label>

        <label className="settings-field">
          <span className="settings-field__label">{t('contactUs.category')}</span>
          <select
            className="settings-select"
            value={category}
            onChange={event => setCategory(event.target.value)}
          >
            <option value="">{t('contactUs.categoryPlaceholder')}</option>
            {CATEGORY_KEYS.map(key => (
              <option key={key} value={key}>
                {t(`contactUs.categories.${key}`)}
              </option>
            ))}
          </select>
        </label>

        <label className="settings-field">
          <span className="settings-field__label">{t('contactUs.message')}</span>
          <textarea
            className={`settings-textarea${messageError ? ' settings-textarea--error' : ''}`}
            value={message}
            onChange={event => setMessage(event.target.value)}
            placeholder={t('contactUs.messagePlaceholder')}
            rows={5}
            aria-invalid={Boolean(messageError)}
            aria-describedby={messageError ? 'contact-message-error' : undefined}
          />
          <FormError message={messageError} id="contact-message-error" />
        </label>

        <Button type="submit" fullWidth disabled={!firstName || !email || !message}>
          {t('contactUs.submitMessage')}
        </Button>
      </form>

      <section className="settings-card" aria-labelledby="contact-info-title">
        <div className="settings-row">
          <span className="settings-row__icon">
            <MapPinIcon size={18} />
          </span>
          <span className="settings-row__copy">
            <span className="settings-row__title" id="contact-info-title">
              {t('contactUs.locationTitle')}
            </span>
            <span className="settings-row__description support-address">
              {t('contactUs.locationAddress')}
            </span>
          </span>
        </div>
        <a className="settings-row support-link" href={`mailto:${supportEmail}`}>
          <span className="settings-row__icon">
            <MailIcon size={18} />
          </span>
          <span className="settings-row__copy">
            <span className="settings-row__title">{t('contactUs.emailSupportTitle')}</span>
            <span className="settings-row__description support-link__value">{supportEmail}</span>
          </span>
        </a>
      </section>
    </MorePage>
  );
}
